/**
 * Copyright © PlanMaster Fogászati Szoftver Kft. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Tamas Neufeld <tamas.neufeld@planmaster3d.com>, 2018
 */

import cloneDeep from 'lodash/cloneDeep';
const _ = { cloneDeep };

export default {

    namespaced: true,

    // Usage: store.state.tabs.activeTab
    state: {
        _debug: true,
        tabs: [],
        activeTab: null,
        componentData: {}, // subscribed components' data
        history: [],
    },

    getters: {
        all: function(state, getters, rootState, rootGetters) {
            return function() {
                return Array.isArray(state.tabs) ? state.tabs : [];
            };
        },
        componentData: function(state, getters, rootState, rootGetters) {
            return function(component) {
                if (typeof state.componentData[component] !== 'undefined') return state.componentData[component];
                else return null;
            };
        },
    },

    actions: {
        /**
         * Creates new tab
         * @param {object} context
         * @param {object} params
         * Use: store.dispatch('tabs/create', {title: 'TITLE', route: 'ROUTE'});
         */
        create: function(context, params) {
            context.commit('create', params);
        },

        /**
         * Opens the tab with given index
         * @param {object} context
         * @param {int} idx
         * Use: store.dispatch('tabs/setActive', IDX);
         */
        setActive: function(context, idx) {
            context.commit('detachData'); // Detach (store) the subscribed components' data of the old tab
            context.commit('setActive', idx); // Set the index of the new tab
            context.commit('attachData'); // Attach (restore) the subscribed components' data of the new tab
            context.commit('setRoute'); // Set the route
        },

        close: function(context, idx) {
            context.commit('close', idx);
        },

        /**
         *
         * @param context
         * @param id
         * Use: store.dispatch('tabs/closeByPatient', ID);
         */
        closeByPatient: function(context, id) {
            if (typeof id !== 'number' || parseInt(id) <= 0) throw new Error('ID parameter is missing, empty or not a positive integer!');
            for (let i in context.state.tabs) {
                let tab = context.state.tabs[i];
                if (typeof tab.patient_id !== 'undefined' && tab.patient_id === id) {
                    context.commit('close', parseInt(i));
                    return;
                }
            }
        },

        /**
         * Opens the tab with the given patient
         * Deprecated
         * @param context
         * @param id
         * Use: store.dispatch('tabs/openByPatient', { id: ID, [route: route]});
         */
        openByPatient: function(context, params) {
            if (typeof params.id !== 'number' || parseInt(params.id) <= 0) throw new Error('ID parameter is missing, empty or not a positive integer!');

            if(!app.$acl.has('modules/patients')) {
                app.$root.showSnackbar({
                    color: 'black',
                    timeout: 3000,
                    text: app.$t('core.not_auth'),
                });
                return;
            }

            context.state.history.unshift(params.id);
            context.state.history = Helper.getArrayUnique(context.state.history);

            for (let i in context.state.tabs) {
                let tab = context.state.tabs[i];
                if (typeof tab.patient_id !== 'undefined' && tab.patient_id === params.id) {
                    app.$store.dispatch('tabs/setActive', parseInt(i));
                    app.$bus.$emit('tab:created');
                    return;
                }
            }
            if(typeof params.route === 'undefined') params.route = '/patient/'+params.id;
            else params.route = '/patient/' + params.id + '/' + params.route;
            app.$store.dispatch('tabs/create', {title: app.$store.getters['patient/data'](params.id, 'name'), route: params.route, patient_id: params.id});
            app.$store.dispatch('tabs/setActive', context.state.tabs.length-1);
            app.$bus.$emit('tab:created');
        },

        openByRoute: function(context, route) {
            if (typeof route !== 'string' || route.trim() === '') throw new Error('Route parameter is missing, empty or not a string!');
            for (let i in context.state.tabs) {
                let tab = context.state.tabs[i];
                if (typeof tab.route !== 'undefined' && tab.route === route) {
                    app.$store.dispatch('tabs/setActive', parseInt(i));
                    app.$bus.$emit('tab:created');
                    return true;
                }
            }
            return false;
        }
    },

    mutations: {
        create: function(state, params) {
            if (typeof params !== 'object' || typeof params.route !== 'string' || params.route.trim() === '') throw new Error('Route parameter is missing, empty or not a string!');
            if (typeof params.title !== 'string' || params.title.trim() === '') throw new Error('Title parameter is missing, empty or not a string!');
            if (typeof params.locked === 'undefined') params.locked = false;
            if (typeof params.locked !== 'boolean') throw new Error('Locked parameter is not boolean');

            let tabData = {
                titleAlias: null,
                stateData: {},
                componentData: {},
            };
            for(let i in params) {
                if(params.hasOwnProperty(i)) {
                    tabData[i] = params[i];
                }
            }
            state.tabs.push(tabData);
        },

        close: function(state, idx) {
            if (parseInt(idx)<0 || !Number.isInteger(idx)) throw new Error('Parameter idx is missing or not a positive integer!');
            if (parseInt(idx) > state.tabs.length) throw new Error('Requested tab not exists');
            if (typeof state.tabs[idx].patient_id !== 'undefined') {
                let historyIndex = state.history.indexOf(state.tabs[idx].patient_id);
                state.history.splice(historyIndex,1);
            }
            state.tabs.splice(idx,1);
            state.activeTab = null;
        },

        subscribe: function(state, params) {
            if (typeof params !== 'object' || typeof params.component !== 'string' || params.component.trim() === '') throw new Error('Parameter component is missing or not a string!');
            if (typeof params.data !== 'object' || params.data === null || Array.isArray(params.data)) throw new Error('Parameter data is missing or not an object!');
            state.componentData[params.component] = params.data;
        },

        unsubscribe: function(state, component) {
            if (typeof component !== 'string' || component.trim() === '') throw new Error('Parameter component is missing or not a string!');
            delete state.componentData[component];
        },

        detachData: function(state) {
            if(state.activeTab === null) return;
            if(typeof state.tabs[state.activeTab] === 'undefined') return;
            if(typeof state.componentData !== 'undefined') state.tabs[state.activeTab].componentData = _.cloneDeep(state.componentData);
        },

        attachData: function(state) {
            if(state.activeTab === null) return;
            if(typeof state.tabs[state.activeTab] === 'undefined') return;
            state.componentData = state.tabs[state.activeTab].componentData;
        },

        setRoute: function(state) {
            if(state.activeTab === null) return;
            if(typeof state.tabs[state.activeTab] === 'undefined') return;
            if(typeof state.tabs[state.activeTab].route !== 'string') throw new Error('Route parameter is missing, empty or not a string!');
            router.push({path: state.tabs[state.activeTab].route, params: {}});
        },

        setActive: function(state, idx) {
            if (parseInt(idx)<0 || !Number.isInteger(idx)) throw new Error('Parameter idx is missing or not a positive integer!');
            if (parseInt(idx) > state.tabs.length) throw new Error('Requested tab not exists');
            state.activeTab = parseInt(idx);
            state.componentData = {};
        },
    },
};
